/* Felicitations animations — GSAP + ScrollTrigger + Lenis (smooth scroll).
 *
 * Usage in liquid/HTML:
 *   <h1 data-anim="fade-up">...</h1>
 *   <h1 data-anim="fade-up" data-delay="0.2" data-duration="1.4">...</h1>
 *   <img data-anim="parallax" data-speed="0.5">
 *   <div data-anim="reveal-image"><img src=...></div>
 *   <div data-anim="scale-in">...</div>
 *   <h2 data-anim="split-lines">Long heading text</h2>
 *   <div data-anim="stagger" data-stagger="0.08"><div>1</div><div>2</div></div>
 *   <section data-anim="pin" data-pin-end="+=1500">...</section>
 *
 * Smooth scroll (Lenis) is enabled by default on desktop, disabled on mobile (<768px) and when prefers-reduced-motion is set.
 * To disable on a specific page: <body class="no-smooth">
 */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function waitForLibs(cb, attempts) {
    attempts = attempts || 0;
    if (window.gsap && window.ScrollTrigger) return cb();
    if (attempts > 100) { console.warn('[fel-anim] GSAP not loaded'); return; }
    setTimeout(function () { waitForLibs(cb, attempts + 1); }, 50);
  }

  ready(function () {
    waitForLibs(init);
  });

  function init() {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var isMobile = window.matchMedia('(max-width: 767px)').matches;
    var noSmooth = document.body.classList.contains('no-smooth');

    // ---------- Lenis smooth scroll ----------
    if (window.Lenis && !prefersReduced && !isMobile && !noSmooth) {
      var lenis = new window.Lenis({
        duration: 1.1,
        easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true,
        smoothTouch: false
      });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
      window.__felLenis = lenis;
    }

    // ---------- helpers ----------
    function num(el, attr, def) {
      var v = parseFloat(el.getAttribute(attr));
      return isNaN(v) ? def : v;
    }
    function trig(el) {
      return {
        trigger: el,
        start: el.getAttribute('data-start') || 'top 85%',
        end: el.getAttribute('data-end') || 'bottom 20%',
        toggleActions: 'play none none none'
      };
    }

    if (prefersReduced) {
      document.querySelectorAll('[data-anim]').forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    // ---------- fade-up / fade-in / scale-in ----------
    document.querySelectorAll('[data-anim="fade-up"]').forEach(function (el) {
      gsap.to(el, {
        opacity: 1, y: 0,
        duration: num(el, 'data-duration', 1.1),
        delay: num(el, 'data-delay', 0),
        ease: 'power3.out',
        scrollTrigger: trig(el)
      });
    });

    document.querySelectorAll('[data-anim="fade-in"]').forEach(function (el) {
      gsap.to(el, { opacity: 1, duration: num(el, 'data-duration', 1.2), delay: num(el, 'data-delay', 0), ease: 'power2.out', scrollTrigger: trig(el) });
    });

    document.querySelectorAll('[data-anim="scale-in"]').forEach(function (el) {
      gsap.to(el, {
        opacity: 1, scale: 1,
        duration: num(el, 'data-duration', 1.3),
        delay: num(el, 'data-delay', 0),
        ease: 'power3.out',
        scrollTrigger: trig(el)
      });
    });

    // ---------- reveal-image (clip + scale image inside container) ----------
    document.querySelectorAll('[data-anim="reveal-image"]').forEach(function (host) {
      var inner = host.querySelector(':scope > *');
      if (!inner) return;
      var tl = gsap.timeline({ scrollTrigger: trig(host) });
      tl.to(inner, { opacity: 1, scale: 1, duration: 1.6, ease: 'power3.out' });
    });

    // ---------- stagger (children animate in sequence) ----------
    document.querySelectorAll('[data-anim="stagger"]').forEach(function (host) {
      var stagger = num(host, 'data-stagger', 0.08);
      gsap.to(host.children, {
        opacity: 1, y: 0,
        duration: 1,
        ease: 'power3.out',
        stagger: stagger,
        scrollTrigger: trig(host)
      });
    });

    // ---------- split-lines (mini SplitText replacement) ----------
    document.querySelectorAll('[data-anim="split-lines"]').forEach(function (el) {
      splitIntoLines(el);
      var lines = el.querySelectorAll('.fel-line-inner');
      gsap.to(lines, {
        y: '0%',
        duration: num(el, 'data-duration', 1.1),
        delay: num(el, 'data-delay', 0),
        ease: 'power3.out',
        stagger: num(el, 'data-stagger', 0.08),
        scrollTrigger: trig(el),
        onStart: function () { el.style.opacity = 1; }
      });
    });

    // ---------- parallax (slow-moving element relative to scroll) ----------
    document.querySelectorAll('[data-anim="parallax"]').forEach(function (el) {
      var speed = num(el, 'data-speed', 0.4); // 0.4 = moves 40% of scroll distance
      gsap.fromTo(el,
        { y: function () { return window.innerHeight * speed * 0.5; } },
        {
          y: function () { return window.innerHeight * speed * -0.5; },
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        }
      );
    });

    // ---------- pin section (sticky scrollytelling — content stays while user scrolls) ----------
    document.querySelectorAll('[data-anim="pin"]').forEach(function (el) {
      var endVal = el.getAttribute('data-pin-end') || '+=' + window.innerHeight;
      ScrollTrigger.create({
        trigger: el,
        start: 'top top',
        end: endVal,
        pin: true,
        pinSpacing: true
      });
    });

    // ---------- pin-scrub (timeline that plays forward/backward as you scroll) ----------
    document.querySelectorAll('[data-anim="pin-scrub"]').forEach(function (host) {
      var inners = host.querySelectorAll('[data-pin-step]');
      if (!inners.length) return;
      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: host,
          start: 'top top',
          end: '+=' + (inners.length * window.innerHeight),
          scrub: 0.6,
          pin: true
        }
      });
      inners.forEach(function (step, i) {
        if (i > 0) {
          tl.from(step, { autoAlpha: 0, y: 60, duration: 1 }, '>-0.5');
        } else {
          step.style.opacity = 1;
        }
      });
    });

    // ---------- bento-gallery (pin + scrub: hero zooms to center, others fade) ----------
    var inDesignMode = window.Shopify && window.Shopify.designMode;
    var bentoIsMobile = window.matchMedia('(max-width: 767px)').matches;
    document.querySelectorAll('[data-anim-bento]').forEach(function (section) {
      var items = section.querySelectorAll('.fel-bento__item');
      if (!items.length) return;
      if (inDesignMode) return;

      // Subtle entry reveal (always)
      gsap.from(items, {
        opacity: 0, y: 30, scale: 0.98,
        duration: 1.0, ease: 'power3.out',
        stagger: 0.06,
        scrollTrigger: { trigger: section, start: 'top 80%', toggleActions: 'play none none none' }
      });

      // Pin + zoom hero only on desktop
      if (bentoIsMobile) return;

      var hero = items[0];
      var others = Array.from(items).slice(1);
      if (!hero) return;

      // compute offset to center hero inside section (recomputed on refresh)
      function centerOffset() {
        var sRect = section.getBoundingClientRect();
        var hRect = hero.getBoundingClientRect();
        return {
          dx: (sRect.left + sRect.width / 2) - (hRect.left + hRect.width / 2),
          dy: (sRect.top + sRect.height / 2) - (hRect.top + hRect.height / 2)
        };
      }

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'center center',
          end: '+=120%',
          scrub: 1,
          pin: true,
          pinSpacing: true,
          invalidateOnRefresh: true,
          anticipatePin: 1
        }
      });

      tl.to(others, {
        opacity: 0,
        scale: 0.92,
        ease: 'none'
      }, 0);

      tl.to(hero, {
        x: function () { return centerOffset().dx; },
        y: function () { return centerOffset().dy; },
        scale: 1.85,
        zIndex: 5,
        ease: 'none'
      }, 0);
    });

    // initial refresh
    ScrollTrigger.refresh();
  }

  // mini SplitText — splits element text into lines (one wrapping span per line)
  function splitIntoLines(el) {
    if (el.dataset.felSplit === '1') return;
    var html = el.innerHTML;
    // wrap each word in a span, then group by offsetTop
    var stripped = html.replace(/\s+/g, ' ');
    var words = stripped.trim().split(' ');
    el.innerHTML = words.map(function (w) {
      return '<span class="fel-word" style="display:inline-block;white-space:pre">' + w + ' </span>';
    }).join('');
    var spans = Array.from(el.querySelectorAll('.fel-word'));
    var lines = [];
    var currentTop = null;
    var currentLine = [];
    spans.forEach(function (s) {
      var top = s.offsetTop;
      if (currentTop === null || Math.abs(top - currentTop) < 4) {
        currentLine.push(s);
        if (currentTop === null) currentTop = top;
      } else {
        lines.push(currentLine);
        currentLine = [s];
        currentTop = top;
      }
    });
    if (currentLine.length) lines.push(currentLine);
    el.innerHTML = '';
    lines.forEach(function (lineSpans) {
      var lineWrap = document.createElement('span');
      lineWrap.className = 'fel-line';
      var inner = document.createElement('span');
      inner.className = 'fel-line-inner';
      lineSpans.forEach(function (ls) { inner.appendChild(ls); });
      lineWrap.appendChild(inner);
      el.appendChild(lineWrap);
    });
    el.dataset.felSplit = '1';
  }
})();
